(() => {
  if (window.riviumPreCheckoutUpsellLoaded) return;
  window.riviumPreCheckoutUpsellLoaded = true;

  const root = window.Shopify?.routes?.root || '/';
  let flowStarted = false;
  let checkoutStarted = false;
  let activeDialog = null;

  const track = (eventName, properties = {}) => {
    window.posthog?.capture(eventName, properties);
    window.dataLayer?.push({ event: eventName, ...properties });
    document.dispatchEvent(new CustomEvent(`rivium:${eventName}`, { detail: properties }));
  };

  const getDecision = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const setDecision = (key, decision) => {
    try {
      window.localStorage.setItem(key, decision);
    } catch {
      return;
    }
  };

  const proceedToCheckout = (properties = {}) => {
    if (checkoutStarted) return;
    checkoutStarted = true;
    track('upsell_checkout_started', properties);
    window.location.assign(`${root}checkout`);
  };

  const isCheckoutTrigger = (element) => {
    if (!(element instanceof Element)) return false;
    return Boolean(element.closest('button[name="checkout"], input[name="checkout"], #checkout-submit'));
  };

  const loadOffer = async () => {
    const sectionUrl = `${root}cart?section_id=pre-checkout-upsell`;
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(sectionUrl, { headers: { Accept: 'text/html' } }),
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    ]);

    if (!sectionResponse.ok || !cartResponse.ok) throw new Error('offer_load_failed');

    const markup = await sectionResponse.text();
    const cart = await cartResponse.json();
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    const dialog = documentFragment.querySelector('[data-pre-checkout-upsell]');

    if (!dialog) return { dialog: null, cart };

    activeDialog?.remove();
    activeDialog = document.importNode(dialog, true);
    document.body.append(activeDialog);

    return { dialog: activeDialog, cart };
  };

  const createProperties = (dialog, cart) => ({
    cart_token: dialog.dataset.cartToken || cart.token || 'anonymous-cart',
    cart_signature: cart.items.map((item) => `${item.key}:${item.quantity}`).join('|'),
    offer_id: dialog.dataset.offerId,
    source_product_id: dialog.dataset.sourceProductId,
    source_product_title: dialog.dataset.sourceProductTitle,
    target_product_id: dialog.dataset.targetProductId,
    target_product_title: dialog.dataset.targetProductTitle,
    target_variant_id: dialog.dataset.targetVariantId,
    regular_price: Number(dialog.dataset.regularPrice),
    offer_price: Number(dialog.dataset.offerPrice),
    discount_percent: Number(dialog.dataset.discountPercent),
    discount_code: dialog.dataset.discountCode,
    primary_purchase_type: cart.items.some((item) => item.selling_plan_allocation) ? 'subscription' : 'one_time'
  });

  const openOffer = (dialog, cart) => {
    const acceptButton = dialog.querySelector('[data-upsell-accept]');
    const declineButton = dialog.querySelector('[data-upsell-decline]');
    const errorMessage = dialog.querySelector('[data-upsell-error]');
    const properties = createProperties(dialog, cart);
    const decisionKey = `rivium-pre-checkout-upsell:${properties.cart_token}:${properties.cart_signature}`;
    let busy = false;

    if (getDecision(decisionKey)) {
      proceedToCheckout({ ...properties, decision: 'previous_decision' });
      return;
    }

    const setBusy = (value) => {
      busy = value;
      acceptButton.disabled = value;
      declineButton.disabled = value;
    };

    const decline = (method) => {
      if (busy) return;
      setDecision(decisionKey, 'declined');
      track('upsell_offer_declined', { ...properties, method });
      proceedToCheckout({ ...properties, decision: 'declined' });
    };

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      decline('escape');
    });

    declineButton.addEventListener('click', () => decline('button'));

    acceptButton.addEventListener('click', async () => {
      if (busy) return;

      setBusy(true);
      errorMessage.hidden = true;

      let addedLineKey = null;

      try {
        const variantId = dialog.dataset.targetVariantId;
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _pre_checkout_upsell: 'true',
                _upsell_offer_id: dialog.dataset.offerId,
                _upsell_source_product_id: dialog.dataset.sourceProductId
              }
            }]
          })
        });

        if (!addResponse.ok) throw new Error('cart_add_failed');

        const addPayload = await addResponse.json();
        const addedItems = Array.isArray(addPayload.items) ? addPayload.items : [addPayload];
        addedLineKey = addedItems[0]?.key || null;

        const discountResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount: properties.primary_purchase_type === 'subscription'
              ? `${dialog.dataset.discountCode},FREE-SHIPPING-APPLIED`
              : dialog.dataset.discountCode
          })        });
        if (!discountResponse.ok) throw new Error('discount_apply_failed');

        const updatedCart = await discountResponse.json();
        const addedItem = updatedCart.items.find((item) => {
          return !item.selling_plan_allocation &&
            item.properties?._pre_checkout_upsell === 'true' &&
            item.properties?._upsell_offer_id === dialog.dataset.offerId;
        });

        if (!addedItem) throw new Error('cart_verify_failed');
        addedLineKey = addedItem.key;

        const expectedPrice = Number(dialog.dataset.offerPrice);
        if (Number(addedItem.final_price) > expectedPrice) {
          await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: addedItem.key, quantity: 0 })
          });
          addedLineKey = null;
          throw new Error('discount_apply_failed');
        }

        setDecision(decisionKey, 'accepted');
        track('upsell_offer_accepted', properties);
        proceedToCheckout({ ...properties, decision: 'accepted' });
      } catch (error) {
        if (addedLineKey) {
          await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: addedLineKey, quantity: 0 })
          }).catch(() => null);
        }
        track('upsell_add_failed', { ...properties, reason: error.message });
        errorMessage.hidden = false;
        setBusy(false);
      }
    });

    dialog.showModal();
    document.body.classList.add('pre-checkout-upsell-open');
    track('upsell_offer_shown', properties);
  };

  const startFlow = async () => {
    if (flowStarted) return;
    flowStarted = true;

    try {
      const { dialog, cart } = await loadOffer();
      if (!dialog) {
        proceedToCheckout({ decision: 'no_offer' });
        return;
      }
      openOffer(dialog, cart);
    } catch (error) {
      track('upsell_add_failed', { reason: error.message, stage: 'offer_load' });
      proceedToCheckout({ decision: 'offer_load_failed' });
    }
  };

  document.addEventListener('click', (event) => {
    if (!isCheckoutTrigger(event.target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    startFlow();
  }, true);

  document.addEventListener('submit', (event) => {
    if (!isCheckoutTrigger(event.submitter)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    startFlow();
  }, true);
})();
